"use client";
import Autoplay from "embla-carousel-autoplay";
import { CornerRightUp } from 'lucide-react';
import Link from "next/link";
import React from "react";
import localLink from "@/lib/localEndPoints";

// components
import { CompanyCard } from "@/components/pages/company/CompanyCard";
import { AboutSection } from "@/components/reusebale-components/AboutSection";
import { BannerCarousel } from "@/components/reusebale-components/bannerCarousel";
import { CallToAction } from "@/components/reusebale-components/CallToAction";
import { Spinner } from "@/components/ui/spinner";

import PositionsCard from "@/components/pages/career/PositionsCard";
import { RecentEvents } from "@/components/pages/events/RecentEvents";
import HeaderTitle from "@/components/reusebale-components/HeaderTitle";
import { NewsUpdateCard } from "@/components/reusebale-components/news&updateCard";
import SocialCard from "@/components/social/SocialCard";
import { Button } from "@/components/ui/button";
import {
    Carousel,
    CarouselContent,
    CarouselItem,
    CarouselNext,
    CarouselPrevious,
} from "@/components/ui/carousel";
import { Separator } from "@/components/ui/separator";

// data fetching endpoints, SWR and types
import endPoints from '@/lib/apiEndPoints';
import { useFetchSWR, useFetchSWRInfinite } from '@/lib/swrConfig';
import { bannerEnum as be } from "@/lib/utils";
import { AboutData } from "@/types/about";
import { BannerData } from "@/types/banner";
import { BlogData } from "@/types/blogs";
import { CircularData } from "@/types/circulars";
import { CommitmentData } from "@/types/commitments";
import { ParentCompanyData } from '@/types/companies';

export default function Home() {
  // banner data
  const { data: bannerData, isLoading } = useFetchSWR(endPoints.BANNER(be.HOME));
  const banner: BannerData = bannerData?.status === 200 ? bannerData.data : {};

  // get parrent company details
  const { data: comRes } = useFetchSWR(endPoints.PARRENT_COMPANY);
  const company: ParentCompanyData = comRes?.status === 200 ? comRes.data : {};

  // about section content
  const { data: aboutRes } = useFetchSWR(company?.id ? endPoints.ABOUT(company.id) : null);
  const about: AboutData = aboutRes?.status === 200 ? aboutRes.data  : {};

   const autoplayPlugin = React.useRef(
      Autoplay({ delay: 5000, stopOnInteraction: false })
    )
  // companies carousel data
  const getCompanies = (pageIndex: number, previousPageData: any) => {
    if (previousPageData && !previousPageData.data.length) return null;
        return endPoints.COMPANIES(pageIndex + 1, 10);
    };
  const { data: companiesData } = useFetchSWRInfinite(getCompanies);
  const companies = companiesData ? companiesData.flatMap((page) => page.data) : [];
  // get lates news updates and blogs
  const getNews = (pageIndex: number, previousPageData: any) => {
    if (previousPageData && !previousPageData.data.length) return null;
        return endPoints.BLOGS(pageIndex + 1, 3);
    };
  const { data: newsData } = useFetchSWRInfinite(getNews);
  const newses = newsData ? newsData.flatMap((page) => page.data) : [];

  // fetch all events
  const getevents = (pageIndex: number, previousPageData: any) => {
  if (previousPageData && !previousPageData.data.length) return null;
      return endPoints.GET_EVENTS(pageIndex + 1, 5, 0);
  };
  const { data: eventsData } = useFetchSWRInfinite(getevents);
  const events = eventsData ? eventsData.flatMap((page) => page.data) : [];

  // fetch circulars
  const { data: circularRes } = useFetchSWR(endPoints.CIRCULARS);
  const circulars = circularRes?.status === 200 ? circularRes.data  : [];

  // fetch social commitments
  const { data: commitmentRes } = useFetchSWR(endPoints.COMMITMENTS (1, 2));
  const commitments = commitmentRes?.status === 200 ? commitmentRes.data  : [];


  // about img
  const aboutImg = about?.media?.[0]?.url;

  return (
    <>
       {
          isLoading ? (
              <div className="h-screen flex items-center justify-center">
              <Spinner />
              </div>
          ) : (
              <BannerCarousel
              images={banner?.media?.map((media) => media.url)}
              title={banner?.title}
              description={banner?.details}
              />
          )
      } 
      <div className="container ">

        {/* about section */}
        {
          (about.title || about.details || aboutImg)  && (
            <div className="mt-6 md:mt-10 lg:mt-16">
            <AboutSection
              title={about?.title}
              description={about?.details}
              
              image={aboutImg || "/default.jpg"}
            />
          </div>
          )
        }

        <Separator className={`my-6 md:my-10 lg:my-16 ${about ? '' : 'border-none'}`}  />


        {/* our companies */}
        {
          companies.length > 0 && (
            <div >
              <HeaderTitle 
                title="Our Companies" 
                url={localLink.COMPANIES}
                btnText="View All"
                  
                  />
              <div className="">
                <Carousel 
                  opts={{loop: true, }}
                  orientation="horizontal" 
                  plugins={[autoplayPlugin.current]}
                  >
                  <CarouselContent className="gap-4 my-1" >
                    {companies.map((company, index) => (
                      <CarouselItem key={index} className="w-full max-w-sm">

                        <CompanyCard company={company} />

                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              </div>

            </div>
          )
        }

        {
          companies.length > 0 && (
            <Separator className="my-6 md:my-10 lg:my-16 " />
          )
        }


        {/* Social Commitment  */}
        {
          commitments.length > 0 && (
            <div >
              <HeaderTitle 
                  title="Corporate Social Responsibility" 
                  url=""
                  btnText=""
                    
                  />
                <div className="space-y-4 md:space-y-8 ">
                  {commitments.slice(0, 2).map((item: CommitmentData) => (
                    <div key={item.id}>
                      <SocialCard {...item} />
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-center">
                  <Link rel="preload"  prefetch href="/social" className=" w-fit mt-4 md:mt-10">
                    <Button
                      variant={"link"}
                      className="cursor-pointer text-primary"
                    >
                      <span className="flex items-center gap-1">
                        View our all Commitments to community 
                        <CornerRightUp size={16} className="rotate-45"/>
                      </span>
                    </Button>
                  </Link>
                </div>
              </div>
          )
        }

        {
          commitments.length > 0 && (
            <Separator className="my-6 md:my-10 lg:my-16 " />
          )
        }


        {/* news and updates */}
        {
          newses.length > 0 && (
            <div className='transition-all duration-300' >
              <HeaderTitle 
                title="News & Updates" 
                url={localLink.NEWS}
                btnText="View All"
                
                />
              <div className="grid grid-cols-1  lg:grid-cols-3  gap-6 ">
                {newses.map((news:BlogData) => (
                  <NewsUpdateCard key={news.id} news={news} />
                ))}
              </div>

            </div>
          )
        }

        {
          newses.length > 0 && (
            <Separator className="my-6 md:my-10 lg:my-16 " />
          )
        }

        {/* events and gallery */}
        {
          events.length > 0 && (
            <div >
            {/* title & button */}
            <HeaderTitle 
              title="Events" 
              url={localLink.EVENT}
              btnText="View All"
              
              />

            {/* recent events */}
            <div className="">
              <RecentEvents events={events} />
            </div>
          </div>
          )
        }

        {
          events.length > 0 && (
            <Separator className="my-6 md:my-10 lg:my-16 " />
          )
        }


        {/* career opportunities */}
        {
          circulars.length > 0 && (
           <div >
            <HeaderTitle 
                title="Open Positions" 
                url=""
                btnText=""
                  
                />
              <div className="w-full max-w-4xl mx-auto">
                {circulars.slice(0, 4).map((item:CircularData) => (
                  <PositionsCard
                    isHome={true}
                    key={item.id}
                    {...item}
                  onApply={() => (item.id)}
                  />
                ))}
              </div>

              <div className="flex items-center justify-center">
                <Link 
                  rel="preload"  prefetch
                  href={localLink.CAREER} className=" w-fit mt-4 md:mt-10">
                  <Button
                    variant={"link"}
                    className="cursor-pointer text-primary"
                  >
                    <span className="flex items-center gap-1">
                      Find all opportunities with us
                      <CornerRightUp size={16} className="rotate-45"/>
                    </span>
                    
                  </Button>
                </Link>
              </div>
            </div>
        )}

        {
          circulars.length > 0 && (
            <Separator className="my-6 md:my-10 lg:my-16 " />
          )
        }

        {/* call to action */}
        <div className=" pb-6 md:pb-8 lg:pb-16 ">
          <CallToAction
            title="Join Us in Making a Difference"
            description="Your support can create lasting change in our communities. Get involved and make an impact today!"
            buttonText="Contact"
          />
        </div>

      </div>
    </>
  );
}
